import { NetworkClientProvider, useGet, useHttp } from '@threatadvisor/react-utils';
import { Client } from '@threatadvisor/react-utils/src/types/generated.Framework';
import { Skeleton } from 'antd';
import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

interface ClientContextProps {
  clients: Client[] | undefined;
  clientsLoading: boolean;
  selectedClientId: string | undefined;
  selectedClientUid: string | undefined;
}

const ClientContext = createContext<ClientContextProps>({
  clients: [],
  clientsLoading: false,
  selectedClientId: undefined,
  selectedClientUid: undefined
});

interface ClientProviderProps {
  children: React.ReactNode;
}

function ClientContextProvider({ children }: ClientProviderProps): JSX.Element {
  const navigate = useNavigate();

  const { clientId: selectedClientId } = useParams();
  const [selectedClientUid, setSelectedClientUid] = useState<string>();
  const location = useLocation();
  const {get: httpGet} = useHttp();

  const onload = async (data: Client[] | undefined) => {
    const uidPattern = '^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$';
    if (data && data.length > 0) {
      if (selectedClientId) {
        const uidMatch = selectedClientId.match(uidPattern);
        if (uidMatch) {
          const foundClient = data.find(c => c.uid === uidMatch[0]);
          if (foundClient) {
            let newPath = window.location.href.replace(uidMatch[0], foundClient.id.toString());
            //check for path including: case (not cases), detection (not detections), does one of these exist (detections, services, supports, or myWork)
            const malformedPatterns = /\/detection\/|\/case\//i;
            if(malformedPatterns.test(newPath)) {
              newPath = newPath.replace('detection', 'detections');
              newPath = newPath.replace('case', 'cases');
            }
            const typePattern = /\/detections\/|\/supports\/|\/services\/|\/myWork\//i;

            if(!typePattern.test(newPath)) {
              const uids = location.pathname.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g);

              if(uids) {
                const caseUid = uids.length > 0 ? uids[1] : null;
                if(caseUid) {
                    await httpGet(
                      `${import.meta.env.VITE_SECURITYEVENT_API}/detectioncase/${caseUid}`,
                        undefined, {'x-client-id': foundClient.id.toString()}      
                      ).then(async (resp) => {
                        if(resp) {
                          newPath = newPath.replace('/cases/', '/cases/detections/');
                        }
                        else {
                          await httpGet(
                          `${import.meta.env.VITE_SECURITYEVENT_API}/servicecase/${caseUid}`,
                          undefined,  {'x-client-id': foundClient.id.toString()}    
                        ).then(async (resp) => {
                          if(resp) {
                            newPath = newPath.replace('/cases/', '/cases/services/');
                          }else {
                            await httpGet(
                              `${import.meta.env.VITE_SECURITYEVENT_API}/supportcase/${caseUid}`,
                              undefined,  {'x-client-id': foundClient.id.toString()}    
                            ).then(() => {
                              newPath = newPath.replace('/cases/', '/cases/supports/')
                            })
                          }  
                        }
                      )
                    }
                  }
                )
              }
            }
            window.location.replace(newPath);
          } 
        }
      }
      }
      // select first
      if (!selectedClientId) {
        // Select First, if nothing is available
        const topClient = data.sort((a, b) =>
          (a.name as string).localeCompare(b.name as string)
        )[0];
        navigate(`${topClient.id}/cases/detections`);
      }
    }
  };

  // Get Initial Data
  const {
    data: clients,
    isLoading: clientsLoading,
    refetch: refetchClients
  } = useGet<Client[]>(`${import.meta.env.VITE_SECURITYEVENT_API}/settings/clients`, {
    key: 'clientlist',
    onSuccess: async (data) => {await onload(data)}
  });

  const value = {
    clients,
    clientsLoading,
    selectedClientId,
    selectedClientUid
  };

  useEffect(() => {
    if (selectedClientId && clients) {
      let cid = clients.find(c => c.uid === selectedClientId)?.id;
      if(cid){
        setSelectedClientUid(clients.find(c => c.id === parseInt(selectedClientId))?.id.toString());
      } else{
        setSelectedClientUid(clients.find(c => c.id === parseInt(selectedClientId))?.uid);
      }
    }

    if (!selectedClientId && location.pathname !== '/account') {
      refetchClients();
    }
  }, [clients, location, selectedClientId, refetchClients]);

  return selectedClientId && clients ? (
    <NetworkClientProvider headers={{ 'x-client-id': selectedClientId }}>
      <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
    </NetworkClientProvider>
  ) : (
    <Skeleton />
  );
}

const ClientContextConsumer = ClientContext.Consumer;

const useClientContext = () => useContext(ClientContext);

export { ClientContextConsumer, ClientContextProvider, useClientContext };

